import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createReminder,
  deleteReminder,
  listReminders,
  updateReminder,
  type ReminderPatch,
} from '../lib/remindersApi';
import { useModules } from './useModules';

/**
 * The caller's reminders, shared by the Reminders page and the global red
 * pre-due banner through the one `['reminders']` key.
 *
 * Same clock-driven overrides as the timers query: staleTime 0 + focus refetch
 * so a second tab never keeps showing a dismissed banner, and a 60s poll so the
 * banner appears within a minute even without any interaction (the 1s
 * `useTimerNow` tick handles the exact threshold crossing in between).
 */
export function useReminders() {
  // No polling while the module is off (every caller — sidebar badge, chat
  // widget, reminder bar, mobile ports — goes through here).
  const enabled = useModules().isOn('reminders');
  return useQuery({
    queryKey: ['reminders'],
    enabled,
    queryFn: listReminders,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
  });
}

function invalidate(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['reminders'] });
}

export function useCreateReminder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      title: string;
      description?: string | null;
      dueAt: string;
      estimatedMinutes?: number | null;
    }) => createReminder(input),
    onSuccess: () => invalidate(qc),
    onError: () => invalidate(qc),
  });
}

export function useUpdateReminder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: ReminderPatch }) => updateReminder(id, patch),
    onSuccess: () => invalidate(qc),
    // A 404 means the row is already gone — refetching drops the phantom row
    // instead of leaving a stuck error on screen (same trick as usePauseTimer).
    onError: () => invalidate(qc),
  });
}

export function useDeleteReminder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteReminder(id),
    onSuccess: () => invalidate(qc),
    onError: () => invalidate(qc),
  });
}
